/**
  * @class meleActorDopplerFollow
  * @description
  *
  * # Purpose
  * Implements `<mele-actor-dopplerfollow>` custom web element for showing speed over measured vehicles
  */

import meleActor from 'meleActor'
const Algo = require('../../msys/msysAV/src_em/algo.js')
const Draw = require('../../msys/msysAV/src_em/draw.js')
const Doppler = require('../../msvc/msvcDoppler/doppler.js')
const meleActorDopplerEvents = require('../meleActorDopplerEvents')

const PROPERTIES = _.extend({}, {
  ets: { description: 'event timestamp - current playback time in miliseconds', type: 'integer' },
  keep_min: { description: 'minimum time to always keep follow in miliseconds', type: 'integer', default: 1000 },
  keep_travel: { description: 'maximum travel distance to keep follow in meters', type: 'number', default: 80 },
  keep_max: { description: 'maximum time to keep follow in miliseconds', type: 'integer', default: 2500 },
}, meleActor._properties)

export default class meleActorDopplerFollow extends meleActor {
  init() {
    this.rectangles = []
    this.events = []
    this.canvas = this.appendChildren({ tag: 'canvas' })
    this.canvas.width = 0
    this.canvas.height = 0
    this.canvas.addEventListener('click', this.canvasClick.bind(this))
    super.init()
  } // init

  message({ action, params }) {
    if(action==='mmDopplerTrigger') {
      this.mmDopplerTrigger(params)
    }else if(action==='mmDopplerEvent') {
      this.mmDopplerEvent(params)
    }
  } // message

  mmDopplerTrigger(params) {
    const { event_dt, calibration, optics } = params
    params.rig = Algo.rig(calibration, optics)
    if(this.events.find(e=>e.event_dt===event_dt)) return // don't allow adding if there is already event with the same ID
    this.events.push(params)
  } // mmDopplerTrigger

  mmDopplerEvent(params) {
    const { event_dt, result, calibration, optics } = params
    if(!(result==='hit' || result==='valid' || result==='dry' || result==='simulator')) return
    params.rig = Algo.rig(calibration, optics)
    const idx = this.events.findIndex(e=>e.event_dt===event_dt)
    if(idx>=0) {
      // overwrite previous event with the same ID (produced by preceding mmDopplerTrigger)
      this.events[idx] = params
    }else{
      this.events.push(params)
    }
  } // mmDopplerEvent

  async etsChanged(ets) {
    // on each video time change recalculate estimated position for each remembered vehicle
    // and display bounding rectangle with speed label

    if(!this.adjustCanvasSize()) return
    Draw.clear(this.canvas)

    this.recalculateEvents(ets)
    this.drawEvents(ets)
  } // etsChanged

  adjustCanvasSize() {
    const videosize = /(\d+)x(\d+)/.exec(this.parentElement?.videosize)
    if(!videosize) return false
    const width = parseInt(videosize[1])
    const height = parseInt(videosize[2])
    if(this.canvas.width !== width) this.canvas.width = width
    if(this.canvas.height !== height) this.canvas.height = height
    return true
  } // adjustCanvasSize

  recalculateEvents(ets) {
    // calculate current position of vehicle
    for(const e of this.events) {
      Object.assign(e, Doppler.estimatePosition(e.rig, e.event, ets))
    }

    // filter out too far vehicles
    this.events = this.events.filter(e=>{
      const { shot_us, speed, sog_speed } = e.event
      const duration_ms = Math.abs(ets - shot_us) / 1_000
      if(duration_ms < this.keep_min) return true
      const travelled_m = Math.abs(speed + sog_speed) * duration_ms / 1_000
      if(travelled_m > this.keep_travel) return false
      if(duration_ms > this.keep_max) return false
      return true
    })

    // sort vehicles by their distance (for overlapping)
    this.events = this.events.sort((a, b)=>a.distance-b.distance)
  } // recalculateEvents

  drawEvents(ets) {
    // keep calculated rectangles for checking overlapping and for clicking on them
    this.rectangles = []
    for(const params of this.events) {
      // prepare rectangle enlarged by few pixels
      const { rig, event } = params
      const rect = Doppler.getBoundaries(rig, event, ets, 0, { h_px: 60, v_px: 40 })
      if(rect.width<=0 || rect.height<=0 || rect.height > this.canvas.height*0.9) continue

      // draw rectangle corners and label with speed
      this.drawEventBounds(rect, params)
      this.drawEventInfo(rect, params)

      // keep list of rectangles for click handling
      this.rectangles.push({ ...rect, params })
    }
  } // drawEvents

  drawEventBounds(rect, params) {
    const color = Doppler.color(params)
    const corner_size = 30
    const corner_thick = 7
    const exclusion = (x, y)=>this.rectangles.find(r=>Algo.insideRect(r, x, y))
    const drawn = Draw.drawCorners(this.canvas, rect, corner_size, color, corner_thick, exclusion)
    if(drawn.all && !(rect.x1<0 && rect.x2>=this.canvas.width && rect.y1<0 && rect.y2>=this.canvas.height)) {
      Draw.drawRectangle(this.canvas, rect, color+'44')
      const x = Math.round((rect.x1+rect.x2)/2)
      const y = Math.round((rect.y1+rect.y2)/2)
      Draw.drawCross(this.canvas, x, y, 50, color, 5)
    }
  } // drawEventBounds

  drawEventInfo(rect, params) {
    const bg = Doppler.background(params)
    const fg = Doppler.foreground(params)
    const speed_kmh = Math.floor(Math.abs(params.event.speed)*3.6)
    const lpn = params?.alpr?.lpn
    // calculate size of label text according to rectangle size
    // const text_size = Math.max(Math.min(128, Math.round((rect.x2-rect.x1+1)/5)), 16)
    const text_size = Math.round((rect.x2-rect.x1+1)/8)
    const height2 = Math.round(text_size*2.2 / 2)
    const height = height2 * 2
    const lpn_width2 = Math.round(text_size*6.6 / 2)
    const lpn_width = lpn_width2 * 2
    const width2 = Math.round((lpn ? height2 + lpn_width : height) / 2)

    // calculate label rectangle
    let cx = Math.round((rect.x1+rect.x2)/2)
    const cy = rect.y1 - height2 - 8
    let x1 = cx - width2
    const x2 = cx + width2
    const y1 = cy - height2
    const y2 = cy + height2
    const boundary = { x1, y1, x2, y2 }

    // check if label is not overlapped by other (already displayed) rectangle
    const label_overlapped = this.rectangles.find(r=>Algo.rectOverlap(boundary, r))
    if(label_overlapped) return

    // draw speed circle and optionally license plate rectangle
    if(lpn) {
      x1 += height2
      Draw.drawRectangle(this.canvas, x1, y1, lpn_width, height, '#FFFFFFA0')
      const text_x = x2 - Math.round((lpn_width - height2) / 2)
      Draw.drawText(this.canvas, lpn, text_size, text_x, cy, 'center', '#000000')
      cx -= width2 - height2
    }

    Draw.drawCircle(this.canvas, cx, cy, height2+1, bg)
    Draw.drawText(this.canvas, speed_kmh, text_size, cx, cy, 'center', fg)
  } // drawEventInfo

  canvasClick(e) {
    const { target, clientX, clientY } = e
    const { x, y } = Draw.canvasXY(target, clientX, clientY)
    for(const r of this.rectangles) {
      if(Algo.insideRect(r, x, y)) {
        return meleActorDopplerEvents.default.prototype.showDetails.call(this, r.params)
      }
    }
  } // canvasClick
} // class meleActorDopplerFollow

meleActorDopplerFollow._properties = PROPERTIES
meleActorDopplerFollow.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-actor-dopplerfollow', meleActorDopplerFollow)
